import {
  normalizePackingScanCode,
  packingScanCodesMatch,
} from '@modules/OrderPreparation/PackingV2/utils/packingAssignKey';
import { usePrintingMethod } from '@modules/configuration/Printers/hooks/usePrintingMethod';
import { usePrinters } from '@modules/configuration/Printers/hooks/usePrinters';
import { Printer } from '@modules/configuration/Printers/models/PrinterModel';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Order } from '../models';
import { hasAnyOrderWithAttachments } from '../utils/orderAttachments';

const resolvePrinterCode = (printers: Printer[], scannedCode: string): string | null => {
  const normalized = normalizePackingScanCode(scannedCode);
  if (!normalized) return null;

  const found = printers.find(
    (printer) =>
      packingScanCodesMatch(printer.code, normalized) || packingScanCodesMatch(String(printer.id), normalized),
  );

  if (!found) return null;
  return found.code?.trim() || String(found.id);
};

export const useAttachmentPrinterScan = (orders: Order[] = []) => {
  const { isPrintNode } = usePrintingMethod();
  const [attachmentPrinterCode, setAttachmentPrinterCode] = useState<string | null>(null);

  const hasOrdersWithAttachments = useMemo(() => hasAnyOrderWithAttachments(orders), [orders]);
  const needsAttachmentPrinterScan = hasOrdersWithAttachments && isPrintNode;
  const isAttachmentPrinterReady = !needsAttachmentPrinterScan || Boolean(attachmentPrinterCode);

  const { printers, isLoading: isPrintersLoading } = usePrinters({
    active: 'true',
    enabled: needsAttachmentPrinterScan && !attachmentPrinterCode,
  });

  useEffect(() => {
    if (hasOrdersWithAttachments && !isPrintNode && !attachmentPrinterCode) {
      setAttachmentPrinterCode('pdf');
    }
  }, [attachmentPrinterCode, hasOrdersWithAttachments, isPrintNode]);

  const scanAttachmentPrinter = useCallback(
    async (code: string): Promise<{ success: boolean; message: string }> => {
      if (isPrintersLoading) {
        return { success: false, message: 'Cargando impresoras...' };
      }

      const resolvedCode = resolvePrinterCode(printers, code);
      if (!resolvedCode) {
        return { success: false, message: 'Impresora de folios no encontrada. Escanea un código válido.' };
      }

      setAttachmentPrinterCode(resolvedCode);
      return { success: true, message: `Impresora de folios confirmada (${resolvedCode})` };
    },
    [isPrintersLoading, printers],
  );

  return {
    hasOrdersWithAttachments,
    needsAttachmentPrinterScan,
    isAttachmentPrinterReady,
    attachmentPrinterCode,
    scanAttachmentPrinter,
    isPrintersLoading,
  };
};
